import {ComponentPublicInstance, defineComponent} from "vue";
import {cloneDeep} from "lodash";
import {alertController, modalController} from "@ionic/vue";
import {deserializeFile, SerializedFile, serializeFile} from "@/utils/file";
import Storage from "@/utils/storage";
import {mapGetters} from "vuex";
import {NavigationGuardNext, RouteLocationNormalized, RouteLocationRaw} from "vue-router";
import {isToday} from "@/utils/date";

declare type NavigationGuardNextCallback = (vm: ComponentPublicInstance) => any;
declare type NavigationGuardReturn = void | Error | RouteLocationRaw | boolean | NavigationGuardNextCallback;

export default defineComponent({
    props: {
        selectedDraft: { type: Object, default: () => null }
    },
    data() {
        return {
            drafts: [] as { [key: string]: any}[],
            currentDraft: null as { [key: string]: any} | null,
            draftFiles: [] as SerializedFile[],
            draftsLoaded: false,
            form: {} as { [key: string]: any },
            files: [] as { file: File; format: string; webPath: string }[]
        }
    },
    computed: {
        ...mapGetters("auth", ["user"])
    },
    watch: {
        selectedDraft: {
            immediate: true,
            async handler(val) {
                await this.loadDraftsFromLocalStorage();

                if (val) {
                    const draftFiles = cloneDeep(val.files);
                    this.files = await Promise.all((draftFiles || []).map(async (file: { file: SerializedFile; format: string; webPath: string}) => ({
                        ...file,
                        file: await deserializeFile(file.file),
                        webPath: file.file.data
                    })));
                    this.form = cloneDeep(val.form);
                    this.draftFiles = draftFiles;
                    this.currentDraft = cloneDeep(val);
                }
            }
        },
        form: {
            async handler(form) {
                await this.loadDraftsFromLocalStorage();

                if (!this.currentDraft) {
                    const hasFormData = Object.values({...form, longitude: undefined, latitude: undefined})
                        .filter(x => x).length;
                    if (hasFormData !== 0) {
                        this.initializeDraft();
                    }
                } else {
                    this.updateDraft();
                }
            },
            deep: true
        },
        files: {
            async handler(files) {
                this.draftFiles = await Promise.all((files || []).map(async (file: {file: File; format: string; webPath: string}) => ({
                    ...file,
                    file: await serializeFile(file.file)
                })));
                if (!this.currentDraft) {
                    this.initializeDraft();
                } else {
                    this.updateDraft();
                }
            },
            deep: true
        }
    },

    methods: {
        /**
         * Initialize draft data to be updated using user actions.
         */
        initializeDraft() {
            this.currentDraft = {
                id: Date.now(),
                userId: this.user.id,
                form: this.form || {},
                // eslint-disable-next-line @typescript-eslint/camelcase
                created_at: new Date(),
                // eslint-disable-next-line @typescript-eslint/camelcase
                form_type: this.$options.name,
                files: this.draftFiles || [],
                status: 'Draft'
            } as any;

            // Save if there is a selected draft (editing an existing draft)
            if (this.selectedDraft) {
                this.saveDraftsToLocalStorage();
            }
        },

        /**
         * Collect all draft data and update local storage.
         */
        async updateDraft() {
            if (this.currentDraft) {
                this.currentDraft.form = this.form;
                this.currentDraft.files = this.draftFiles || [];

                // Save if there is a selected draft (editing an existing draft)
                if (this.selectedDraft) {
                    await this.saveDraftsToLocalStorage();
                }
            }
        },

        /**
         * Remove current draft and save to local storage.
         */
        clearDraft() {
            if (this.currentDraft) {
                this.drafts = this.getOtherDrafts();
                this.currentDraft = null;
                this.saveDraftsToLocalStorage();
            }
        },

        // Get all drafts except the current one.
        getOtherDrafts() {
            return (this.drafts as any[]).filter((draft) => draft.id !== this.currentDraft?.id);
        },

        /**
         * Save this.drafts array to local storage.
         */
        async saveDraftsToLocalStorage() {
            const exists = this.drafts.some((draft) => draft.id === this.currentDraft?.id)
            if (this.currentDraft && !exists) {
                this.drafts.push(this.currentDraft);
            } else if (this.currentDraft) {
                this.drafts = (this.drafts as any[]).map(x => x.id === this.currentDraft?.id ? this.currentDraft : x);
            }

            await Storage.set({
                key: 'drafts',
                value: JSON.stringify(this.drafts)
            });
        },

        /**
         * Load drafts from local storage.
         */
        async loadDraftsFromLocalStorage() {
            if (this.draftsLoaded) {
                return;
            }
            this.draftsLoaded = true;
            const data = (await Storage.get({ key: 'drafts' }))?.value;
            this.drafts = JSON.parse(data || '[]');
        },

        /**
         * Clear draft and navigate to submissions.
         */
        clearDraftAndRedirect() {
            this.clearDraft();
            this.$router.replace({name: 'Submissions'});
            if (this.selectedDraft) {
                return modalController.dismiss()
            }
        },

        /**
         * Return true when there's something entered in the form or files present.
         */
        hasUnsavedChanges() {
            try {
                const checkMapData = Object.values({ ...(this.form || {}), longitude: undefined, latitude: undefined });
                const hasFormData = checkMapData.filter((x: any) => x !== null && x !== undefined && x?.length !== 0).length > 0;
                const hasFiles = (this.files || []).length > 0;
                return hasFormData || hasFiles;
            } catch (e) {
                return false;
            }
        },

        /**
         * Save current form as draft.
         */
        async saveAsDraft() {
            // Ensure drafts loaded
            if (this.loadDraftsFromLocalStorage) {
                await this.loadDraftsFromLocalStorage();
            }

            if (!this.currentDraft && this.initializeDraft) {
                this.initializeDraft();
            }

            this.saveDraftsToLocalStorage();

            const savedAlert = await alertController.create({
                header: this.$t('Saved'),
                message: this.$t('Your submission was saved as a draft. You can find it under My Submissions / Drafts.'),
                buttons: [ { text: this.$t('OK') } ]
            });

            await savedAlert.present();
        },

        /**
         * Checks internet connection and save as draft if no internet.
         */
        async checkInternetConnection () {
            if (!navigator.onLine) {
                const noInternetAlert = await alertController.create({
                    header: this.$t('No Internet Connection'),
                    message: this.$t('There is no active internet connection. Your submission will be saved as a draft and can be found under My Submissions / Drafts section. Please re-submit once you have internet access.'),
                    buttons: [
                        {
                            text: this.$t('OK'),
                            handler: async () => {
                                await this.saveAsDraft();
                            }
                        }
                    ]
                });

                await noInternetAlert.present();

                this.$router.replace({ name: 'Submissions'})

                throw 'No Internet';
            }
        },

        // Guard router navigation: if the user has unsaved form data, ask to save as draft
        handleBeforeRouteLeave(to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext): NavigationGuardReturn | Promise<NavigationGuardReturn> {
            if (this.hasUnsavedChanges()) {
                alertController.create({
                    header: this.$t('Unsaved changes'),
                    message: this.$t('You have not finished your submission. Save as draft ?'),
                    buttons: [
                        {
                            text: this.$t('No'),
                            role: 'cancel',
                            handler: () => {
                                // If user chooses not to save, clear the draft (if any) and continue navigation
                                this.clearDraft();
                                next();
                            }
                        },
                        {
                            text: this.$t('Yes'),
                            handler: async () => {
                                // Save current form as draft then continue navigation
                                await this.saveAsDraft();
                                next();
                            }
                        }
                    ]
                }).then(alert => alert.present());
            } else {
                next();
            }
        },

        restoreObservationDate() {
            try {
                const date = JSON.parse(localStorage.getItem('differentDate') || '{}');
                if (date?.createdAt && isToday(new Date(date.createdAt))) {
                    (this as any).differentDate = true;
                    (this as any).datetime = { date: date.date, time: date.time };
                }
            } catch (e) {
                console.log(e);
            }
        },

        persistObservationDate() {
            if ((this as any).differentDate) {
                localStorage.setItem('differentDate', JSON.stringify({
                    ...(this as any).datetime,
                    createdAt: new Date().toISOString()
                }));
            }
        },
    },
});
